/**
 * Sprint T-18 · Notification Studio · Playground · Right panel.
 *
 * 4 tabs: Desktop preview · Mobile preview · Text · JSON (intent shape).
 * Preview uses iframe sandbox (no scripts). Zero deps.
 */
import React, { useState } from "react";
import { Monitor, Smartphone, FileText, Braces, AlertTriangle } from "lucide-react";

const TABS = [
  { key: "desktop", label: "Desktop", icon: Monitor },
  { key: "mobile", label: "Mobile", icon: Smartphone },
  { key: "text", label: "Text", icon: FileText },
  { key: "json", label: "JSON", icon: Braces },
];

const IFRAME_SANDBOX = ""; // strictest · no scripts · no forms · no top-nav

export default function PlaygroundRightPanel({
  preview,
  intentJson,
  loading,
  error,
}) {
  const [tab, setTab] = useState("desktop");

  const bodyHtml = preview?.body_html || "";
  const bodyText = preview?.body_text || "";
  const subject = preview?.subject || "";

  return (
    <div
      className="flex flex-col gap-3 rounded border border-[var(--ag-border)] bg-[var(--ag-panel)] p-4"
      data-testid="studio-playground-right-panel"
    >
      {/* Subject preview strip */}
      <div className="rounded border border-[var(--ag-border)]/70 bg-black/20 p-2">
        <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
          Subject Preview
        </div>
        <div
          className="mt-1 truncate font-mono text-xs text-[var(--ag-text)]"
          data-testid="studio-playground-preview-subject"
        >
          {subject || (loading ? "…" : "—")}
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 border-b border-[var(--ag-border)]"
        role="tablist"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              onClick={() => setTab(t.key)}
              data-testid={`studio-playground-preview-${t.key}-tab`}
              className={`flex items-center gap-1 rounded-t px-3 py-1.5 text-[11px] transition-colors ${
                on
                  ? "border-b-2 border-[var(--ag-gold)] text-[var(--ag-text)]"
                  : "text-[var(--ag-text-dim)] hover:text-[var(--ag-text)]"
              }`}
            >
              <Icon size={11} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div
          className="flex items-center gap-2 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-[11px] text-rose-200"
          data-testid="studio-playground-preview-error"
        >
          <AlertTriangle size={12} />
          <span className="truncate">{error}</span>
        </div>
      )}

      {/* Warnings */}
      {preview?.warnings?.length > 0 && (
        <div
          className="rounded border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-[11px] text-amber-200"
          data-testid="studio-playground-preview-warnings"
        >
          {preview.warnings.slice(0, 3).map((w, i) => (
            <div key={i} className="font-mono">
              · {w}
            </div>
          ))}
        </div>
      )}

      {/* Preview bodies */}
      <div className="min-h-[380px]">
        {tab === "desktop" && (
          <IframePreview
            html={bodyHtml}
            width={720}
            testid="studio-playground-preview-iframe-desktop"
          />
        )}
        {tab === "mobile" && (
          <IframePreview
            html={bodyHtml}
            width={375}
            testid="studio-playground-preview-iframe-mobile"
          />
        )}
        {tab === "text" && (
          <pre
            className="max-h-[500px] overflow-auto rounded bg-black/40 p-3 font-mono text-xs text-[var(--ag-text)]"
            data-testid="studio-playground-preview-text-body"
          >
            {bodyText || "(sin body_text)"}
          </pre>
        )}
        {tab === "json" && (
          <pre
            className="max-h-[500px] overflow-auto rounded bg-black/40 p-3 font-mono text-[11px] text-[var(--ag-text)]"
            data-testid="studio-playground-preview-json"
          >
            {JSON.stringify(intentJson || {}, null, 2)}
          </pre>
        )}
      </div>

      {preview && (
        <div className="flex items-center gap-3 text-[10px] text-[var(--ag-text-dim)]">
          <span data-testid="studio-playground-preview-locale">
            locale: <b className="text-[var(--ag-text)]">{preview.resolved_locale}</b>
          </span>
          <span data-testid="studio-playground-preview-override">
            override: <b className={preview.used_override ? "text-emerald-300" : "text-[var(--ag-text)]"}>{preview.used_override ? "sí" : "no"}</b>
          </span>
        </div>
      )}
    </div>
  );
}

function IframePreview({ html, width, testid }) {
  return (
    <div className="flex justify-center rounded border border-[var(--ag-border)]/50 bg-white p-2">
      <iframe
        title="preview"
        sandbox={IFRAME_SANDBOX}
        srcDoc={html || "<i style='color:#888;font-family:sans-serif;padding:8px'>Sin contenido</i>"}
        style={{
          width: `${width}px`,
          maxWidth: "100%",
          height: "480px",
          border: 0,
          background: "white",
        }}
        data-testid={testid}
      />
    </div>
  );
}
